import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from 'api'
import { DateFilter } from './date-filter'
import { DropdownFilter } from './dropdown-filter'
import {
  IntegrationAssignedFilter,
  IntegrationFilter,
  IntegrationFilterValue,
} from './filter-types'
import { TextFilter } from './text-filter'

interface Props {
  filter: IntegrationAssignedFilter
  filterOption: IntegrationFilter
  onFilterChange: (
    values: IntegrationFilterValue[],
    filter: IntegrationAssignedFilter,
  ) => Promise<void>
  onFilterDelete: (assignedFilter: IntegrationAssignedFilter) => void
  previousYearsRestriction?: number
  integrationSlug?: string
  connectorId?: string
}

export const FilterRenderer = ({
  filter,
  filterOption,
  onFilterChange,
  onFilterDelete,
  previousYearsRestriction,
  integrationSlug,
  connectorId,
}: Props) => {
  const {
    data: loadedOption = filterOption,
    error,
    isLoading,
  } = useQuery({
    queryKey: [
      'integrations',
      integrationSlug,
      'filters',
      connectorId,
      'dynamic',
      filterOption.filterKey,
    ],
    queryFn: async () => {
      if (!filterOption.dynamic) {
        return filterOption
      }

      const response = await api.get<IntegrationFilter[]>(
        `/api/v1/integrations/${integrationSlug}/filters/${connectorId}/dynamic/${filterOption.filterKey}`,
      )

      return response.data.find(o => o.id === filterOption.id) || filterOption
    },
    enabled: !!connectorId && !!integrationSlug,
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60,
  })

  const filterError = useMemo(
    () => (!isLoading && error ? 'Unable to retrieve filter options' : false),
    [isLoading, error],
  )

  switch (filterOption.filterType) {
    case 'dropdown':
      return (
        <DropdownFilter
          filter={filter}
          filterOption={loadedOption}
          onFilterChange={(...args) => void onFilterChange(...args)}
          onFilterDelete={onFilterDelete}
          error={filterError}
          isLoading={isLoading}
        />
      )
    case 'text':
      return (
        <TextFilter
          filter={filter}
          filterOption={loadedOption}
          onFilterChange={(...args) => void onFilterChange(...args)}
          onFilterDelete={onFilterDelete}
          error={filterError}
        />
      )
    case 'date':
    default:
      return (
        <DateFilter
          filter={filter}
          filterOption={loadedOption}
          onFilterChange={(...args) => void onFilterChange(...args)}
          onFilterDelete={onFilterDelete}
          previousYearsRestriction={previousYearsRestriction}
          integrationSlug={integrationSlug}
        />
      )
  }
}
